<script setup>
import { ref, onBeforeMount, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useStore } from 'vuex'
import { API, HOST } from '@/utils/http'
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core'
import * as z from 'zod'
import { useForm } from 'vee-validate'
import { toTypedSchema } from '@vee-validate/zod'
import parsePhoneNumber from 'libphonenumber-js'
import { useAuth } from '@/composables/useAuth'

import { Input } from '@/components/ui/input'
import { Form, FormField, FormItem, FormControl, FormLabel, FormMessage } from '@/components/ui/form'
import { Button } from '@/components/ui/button'
import { toast } from '@/components/ui/toast'

import googlePlayBadge from '@/assets/img/google-play-badge.png'
import appStoreBadge from '@/assets/img/app-store-badge.svg'

const breakpoints = useBreakpoints(breakpointsTailwind)

const router = useRouter()
const route = useRoute()
const inviteCode = route.params.inviteCode

const store = useStore()
const { initAuthListener, currentUser } = useAuth()
const isLoggedIn = computed(() => store.getters.getIsLoggedIn)

const invite = ref(null)
const isShowForm = ref(false)
const isLoading = ref(true)
const inviteError = ref(null)
const firebaseUser = ref(null)
const isMobile = breakpoints.sm || breakpoints.md
const joinFromWeb = ref(isMobile)
const initialValues = ref(null)

const openApp = () => {
  const isAndroid = /Android/i.test(navigator.userAgent) || navigator.platform.toLowerCase().includes('android');

  if (isAndroid) {
    const intentUrl = window.location.href.replace('https', 'intent')
    window.location.href = `${intentUrl}#Intent;scheme=https;package=com.earthpbc.explorer;end`
  } else {
    let url = `earthpbc://${window.location.host}/invite/${inviteCode}`
    window.location.href = url
  }
}

const phoneFieldSchema = z
  .string()
  .optional()
  .nullable()
  .transform((value, ctx) => {
    if (!value) return null

    const phoneNumber = parsePhoneNumber(value, { defaultCountry: 'US' })
    if (!phoneNumber?.isValid()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Invalid or incomplete phone number',
      })
      return z.NEVER
    }

    return phoneNumber.number
  })

const formSchema = ref(z.object({
  first_name: z.string().min(1, 'First name is required').max(255),
  last_name: z.string().min(1, 'Last name is required').max(255),
  email: z.string().email('Invalid email address').optional().nullable(),
  phone: phoneFieldSchema,
}))

/**
 * Join project flow — user must already be authenticated.
 * If not logged in, redirect to auth page first.
 */
const joinProject = async () => {
  if (!currentUser.value) {
    router.push({ name: 'Auth', query: { redirect: `/invite/${inviteCode}` } })
    return
  }

  // Verify invite belongs to current user
  const token = await currentUser.value.getIdToken()
  await getInvite(token)

  if (inviteError.value === 403) {
    toast({
      title: 'Unable to join',
      description: 'Wrong account.',
      variant: 'destructive'
    })
    return
  }

  firebaseUser.value = currentUser.value

  const member = store.getters['user/getMember']
  const fbUser = currentUser.value

  const values = {
    first_name: invite.value?.first_name || member?.first_name || '',
    last_name: invite.value?.last_name || member?.last_name || '',
    email: invite.value?.email || member?.email || fbUser?.email || '',
    phone: invite.value?.phone || member?.phone || fbUser?.phoneNumber || ''
  }

  const hasName = values.first_name && values.last_name
  const hasContact = values.email || values.phone

  if (hasName && hasContact) {
    await onSubmit(values)
    return
  }

  isShowForm.value = true
  initialValues.value = values
}

const back = () => {
  isShowForm.value = false
  firebaseUser.value = null
}

const close = () => {
  if (!isLoggedIn.value) {
    router.push({ name: 'Auth' })
  } else {
    router.push({ name: 'Dashboard' })
  }
}

const getInvite = async (firebase_token) => {
  try {
    const response = await API({
      method: 'GET',
      url: firebase_token
        ? `${HOST}/invite/${inviteCode}?firebase_token=${firebase_token}`
        : `${HOST}/invite/${inviteCode}`
    })
    invite.value = response.data ? response.data : null
    inviteError.value = null
  } catch (error) {
    if (error.response?.status !== 403) {
      invite.value = null
    }
    inviteError.value = error.response?.status || 'error'
  }
}

onBeforeMount(async () => {
  await getInvite()
  isLoading.value = false

  initAuthListener(async (user) => {
    if (user) {
      const token = await user.getIdToken()
      await getInvite(token)

      firebaseUser.value = user
    }
  })
})

async function onSubmit(values) {
  try {
    const idToken = await firebaseUser.value.getIdToken(true)

    const result = await store.dispatch('user/handleAuthentication', {
      firebaseToken: idToken,
      inviteCode: inviteCode,
      inviteDetails: {
        first_name: values.first_name,
        last_name: values.last_name,
        email: values.email,
        phone: values.phone
      }
    })

    if (result.success) {
      router.push({ name: 'Dashboard' })
    } else {
      console.error('Authentication failed:', result.reason)

      toast({
        title: 'Sign in failed',
        description: result.reason === 'error'
          ? 'Failed to process invitation. Please try again.'
          : `Authentication failed: ${result.reason}`
      })
    }
  } catch (error) {
    console.error('Error submitting invite form:', error)

    toast({
      title: 'Sign in failed',
      description: error.response?.data?.detail || 'Failed to process invitation. Please try again.'
    })
  }
}
</script>

<template>
  <div class="">
    <div v-if="!joinFromWeb" class="flex flex-col items-center justify-center p-4 md:p-6 lg:p-8">
      <h1 class="text-xl font-semibold">Get the Earth App</h1>
      <p class="text-gray-500 text-center max-w-lg">
        If you don't have the app, you can download it here:
      </p>

      <div class="flex flex-col md:flex-row space-y-3 md:space-y-0 md:space-x-4 my-5">
        <Button
          as="a"
          :href="'https://play.google.com/store/apps/details?id=com.earthpbc.explorer'"
          target="_blank"
          size="lg"
          class="p-0 bg-transparent hover:bg-gray-100 transition inline-flex items-center justify-center"
        >
          <img
            :src="googlePlayBadge"
            alt="Get it on Google Play"
            class="h-16"
          />
        </Button>

        <Button
          as="a"
          :href="'https://apps.apple.com/us/app/earth-pbc/id1590563602'"
          target="_blank"
          size="lg"
          class="p-0 bg-transparent hover:bg-gray-100 transition inline-flex items-center justify-center"
        >
          <img
            :src="appStoreBadge"
            alt="Download on the App Store"
            class="h-12"
          />
        </Button>
      </div>

      <p class="text-gray-500 text-center max-w-lg my-4">
        Or open the app if you already have it installed.
      </p>
      <Button
        size="lg"
        @click="openApp"
      >
        Open in Earth App
      </Button>

      <div class="mt-6">
        <Button
          size="lg"
          variant="link"
          @click="joinFromWeb = true"
        >
          Join invite on the web
        </Button>
      </div>
    </div>

    <div v-else class="p-1">
      <div class="mb-4">
        <img src="@/assets/img/earth-art.png" alt="Earth" class="md:max-w-full md:w-[400px]">
      </div>

      <div>

        <div v-if="isLoading" class="md:p-1 text-center">
          <p class="mt-4 mb-3 text-gray-500">Loading invite...</p>
        </div>

        <div v-else-if="!isShowForm" class="md:p-1">
          <div v-if="inviteError === 409" class="text-center">
            <p class="mt-4 mb-3">This invite has already been accepted.</p>
            <Button @click="close">
              Close
            </Button>
          </div>

          <div v-else-if="invite" class="mb-4 text-left">
            <div class="mt-4">
              <h3 class="mb-2">Hi, <b>{{ invite.fullname }}</b></h3>
              <p>
                {{ invite.inviter_name }} invited you to join <br />
                the <b>{{ invite.project_name }}</b>.
              </p>
            </div>
            <div class="space-x-2 mt-6">
              <Button size="lg" class="font-semibold" @click="joinProject">
                Join now
              </Button>
              <Button size="lg" variant="outline" @click="close">
                Close
              </Button>
            </div>
          </div>

          <div v-else class="text-center">
            <p class="mt-4 mb-3">Invite not found or has expired.</p>
            <Button @click="close">
              Close
            </Button>
          </div>
        </div>

        <div v-if="isShowForm">
          <div class="mb-3">
            <h2 class="font-bold">Complete your profile</h2>
            <p class="text-gray-500 text-sm">Please provide your details to complete your profile</p>
          </div>

          <Form
            v-if="initialValues"
            :form-schema="formSchema"
            :initial-values="initialValues"
            @submit="onSubmit"
            class="space-y-4"
          >
            <FormField name="first_name" v-slot="{ componentField }">
              <FormItem>
                <FormLabel>First Name</FormLabel>
                <FormControl>
                  <Input v-bind="componentField" />
                </FormControl>
                <FormMessage />
              </FormItem>
            </FormField>

            <FormField name="last_name" v-slot="{ componentField }">
              <FormItem>
                <FormLabel>Last Name</FormLabel>
                <FormControl>
                  <Input v-bind="componentField" />
                </FormControl>
                <FormMessage />
              </FormItem>
            </FormField>

            <FormField name="email" v-slot="{ componentField }">
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input v-bind="componentField" />
                </FormControl>
                <FormMessage />
              </FormItem>
            </FormField>

            <FormField name="phone" v-slot="{ componentField }">
              <FormItem>
                <FormLabel>Phone</FormLabel>
                <FormControl>
                  <Input v-bind="componentField" />
                </FormControl>
                <FormMessage />
              </FormItem>
            </FormField>

            <div class="flex justify-end space-x-2">
              <Button type="button" variant="ghost" @click="back">Back</Button>
              <Button type="submit">Save</Button>
            </div>
          </Form>
        </div>
      </div>
    </div>
  </div>
</template>
